/**
 * Braze changeUser(braze_external_id) when a gated Web identify id is present.
 * Call from auth completion handlers, or from CF-safe handoff after live GET /current_profile.
 * Do not invent an id from CDN-cached HTML, router hooks, or network_user_id.
 * Server pilot (WebIdentifyPilot) is the gate for whether the id is present.
 *
 * Allowed call sites: useRegister (email sign-up),
 * registration/sign-in-elements/sign-in-form (sessionStorage before redirect),
 * campaigns/series-lead-elements/forms/sign-in-form (in-page login, no redirect),
 * braze/web_sdk_session_handoff (sessionStorage or live /current_profile).
 * Bootstrap order when id present: enableSDK → initialize → changeUser → openSession.
 */

/** Must match SESSION_KEY in braze/web_sdk_bootstrap.js */
export const BRAZE_EXTERNAL_ID_SESSION_KEY = 'braze_external_id';

/**
 * Stash braze_external_id for changeUser on the post-login landing page (CDN-safe).
 * @param {string|null|undefined} brazeExternalId Braze wire external id from Livewire auth payload
 */
export function stashBrazeExternalIdForRedirect(brazeExternalId) {
  if (!brazeExternalId || typeof brazeExternalId !== 'string') return;

  try {
    sessionStorage.setItem(BRAZE_EXTERNAL_ID_SESSION_KEY, brazeExternalId);
  } catch (_e) {
    // sessionStorage unavailable (e.g. private browsing)
  }
}

/**
 * @param {string|null|undefined} brazeExternalId Braze wire external id from Livewire auth payload
 */
export function changeBrazeUserIfEnabled(brazeExternalId) {
  if (!brazeExternalId || typeof brazeExternalId !== 'string') return;

  const sdk = window.braze;
  if (!sdk) {
    stashBrazeExternalIdForRedirect(brazeExternalId);
    if (typeof window.LivewireBrazeEnsureSdkAndIdentify === 'function') {
      window.LivewireBrazeEnsureSdkAndIdentify(brazeExternalId);
    }
    return;
  }
  if (typeof sdk.enableSDK === 'function') {
    sdk.enableSDK();
  }
  if (typeof sdk.isInitialized === 'function' && !sdk.isInitialized()) {
    if (typeof window.LivewireBrazeEnsureSdkAndIdentify === 'function') {
      window.LivewireBrazeEnsureSdkAndIdentify(brazeExternalId);
    }
    return;
  }

  sdk.changeUser(brazeExternalId);
  if (typeof sdk.openSession === 'function') {
    sdk.openSession();
  }
}

/**
 * Wipe local Braze data then disable SDK (real sign-out path).
 * Also clears sessionStorage braze_external_id so guest handoff cannot re-inject.
 */
export function wipeBrazeSdkOnSignOut() {
  try {
    sessionStorage.removeItem(BRAZE_EXTERNAL_ID_SESSION_KEY);
  } catch (_e) {
    // sessionStorage unavailable
  }
  if (typeof window.LivewireBrazeWipeOnSignOut === 'function') {
    window.LivewireBrazeWipeOnSignOut();
    return;
  }
  const sdk = window.braze;
  if (!sdk) return;
  if (typeof sdk.wipeData === 'function') {
    sdk.wipeData();
  }
  if (typeof sdk.disableSDK === 'function') {
    sdk.disableSDK();
  }
}
